/**
 * Session Metadata Service
 *
 * Fetches the lightweight session view from ac-mvp — game title,
 * system, sessionNumber, scheduledTime, GM name, and the expected
 * player roster with per-player `hasCharacter` flags.
 *
 * Endpoint: GET <platform>/api/sessions/[id]/vtt-meta (authenticated
 * via the VTT JWT in the Bearer header).
 *
 * Used by TopBar (game title + session number breadcrumb) and the
 * ChatPanel roster (merging expected-but-absent players with the
 * live socket participant list).
 */

export interface VTTExpectedPlayer {
  userId: string
  displayName: string
  avatarUrl: string | null
  /** True iff the player has at least one Character with status
   *  'APPROVED' on the linked Game. Drives the "no character yet"
   *  indicator in the participants list. */
  hasCharacter: boolean
}

export interface VTTSessionMeta {
  sessionId: string
  gameId: string
  gameTitle: string
  gameSystem: string
  sessionNumber: number | null
  scheduledTime: string | null
  status: string
  gmId: string
  gmName: string
  /** Primary game banner (same image as the landing-page card). Used as
   *  the backdrop of the hero loading screen. Null when unset. */
  gameImage: string | null
  /** Secondary hero treatment, preferred over `gameImage` for the
   *  loading screen when present. Null when unset. */
  heroImage: string | null
  expectedPlayers: VTTExpectedPlayer[]
}

const metaCache = new Map<string, VTTSessionMeta>()

function getMainPlatformUrl(): string {
  return import.meta.env.VITE_MAIN_PLATFORM_URL || 'https://arcanecircle.games'
}

/**
 * Fetch session metadata. Cached for the lifetime of the tab — call
 * `clearSessionMetaCache()` if the session changes mid-app (rare,
 * the URL would have to change).
 */
export async function fetchSessionMeta(
  sessionId: string,
  token: string,
): Promise<VTTSessionMeta | null> {
  const cached = metaCache.get(sessionId)
  if (cached) return cached

  const platformUrl = getMainPlatformUrl()

  try {
    const response = await fetch(
      `${platformUrl}/api/sessions/${sessionId}/vtt-meta`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    )

    if (!response.ok) {
      console.warn(
        `[sessionMetaService] Failed to fetch session ${sessionId} meta: ${response.status}`,
      )
      return null
    }

    const data: VTTSessionMeta = await response.json()
    metaCache.set(sessionId, data)
    return data
  } catch (error) {
    console.error(
      `[sessionMetaService] Error fetching session ${sessionId} meta:`,
      error,
    )
    return null
  }
}

export function getCachedSessionMeta(sessionId: string): VTTSessionMeta | null {
  return metaCache.get(sessionId) ?? null
}

export function clearSessionMetaCache(): void {
  metaCache.clear()
}
